import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'next-i18next'
import {
  EmtAccordion,
  EmtButton,
  EmtCheckbox,
  EmtDistance,
  EmtIcon,
  EmtTypography,
  useAccordion,
} from 'emt-components-v2'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useDisplayDistance } from '@/hooks'
import AnimalIncrement from '@/components/PetSitter/AnimalIncrement'
import { ServiceSection } from '../reservations/NewRequest/ServiceSection'
import { useIsResponsive } from '@/hooks'
// Types
import { styled, SxProps } from '@mui/material/styles'
import AddressAutocomplete from '@/components/EMT/AddressAutocomplete'
import { FiltersValues } from '@/modules/PetSitter/searchServiceHelper'
import useEnterKey from '@/hooks/useEnterKey'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import { useServiceSlots } from '@/hooks/useServiceSlots'

import {
  ServiceSlug,
  getSlotType,
  makeBlankSlot,
} from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'

// Define the OrderFiltersNames type if not already defined
type OrderFiltersNames =
  | 'base'
  | 'service'
  | 'period'
  | 'pets'
  | 'distance'
  | 'price'
  | 'petCount'
  | 'sizes'
  | 'features'

export type FiltersHandle = {
  submitDraft: () => Promise<void>
}

type Props = React.PropsWithChildren<{
  filtersValues: FiltersValues
  onFiltersValuesChange: (newValues: Partial<FiltersValues>) => void
  shortFilter: OrderFiltersNames
  isLoading?: boolean
  manualRequest?: boolean
  onEraseFilter: () => void
  onSearchButtonClick: (filtersValues?: FiltersValues) => void
  onClose: () => void
  activeFilterSidebar: boolean
}>

const accordionCheckboxContainerStyle: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row-reverse',
  marginRight: 0,
  gap: '10px',
}

const ActionButtonsWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '19px',
  padding: '15px',
}))

// Adjust the features array to have correct slugs
const features = [
  { slug: 'supportPuppies', title: 'Accepte les chiots', selected: false },
  { slug: 'haveDog', title: 'Ne possède pas de chien', selected: false },
  { slug: 'haveOwnANeuteredMaleDog', title: 'Ne possède pas de chien \nmâle castré', selected: false },
  { slug: 'haveOwnAnUnNeuteredMaleDog', title: 'Ne possède pas de chien \nmâle non castré', selected: false },
  { slug: 'haveOwnASpayedFemaleDog', title: 'Ne possède pas de chien \nfemelle stérilisée', selected: false },
  { slug: 'haveOwnAnUnspayedFemaleDog', title: 'Ne possède pas de chien \nfemelle non stérilisée', selected: false },
  { slug: 'haveCat', title: 'Ne possède pas de chat', selected: false },
  { slug: 'liveInHouse', title: "Maisons uniquement \n(pas d'appartement)", selected: false },
  { slug: 'haveChildren', title: "N'a pas d’enfants en \nbas âge", selected: false },
]

const petSizes = [
  { slug: 'small', title: 'Petit (0 à 9 kg)', selected: false },
  { slug: 'medium', title: 'Moyen (10 à 24 kg)', selected: false },
  { slug: 'big', title: 'Grand (25 à 44 kg)', selected: false },
  { slug: 'very-big', title: 'Très grand (44 à 74 kg)', selected: false },
  { slug: 'giant', title: 'Géant (75 kg ou plus)', selected: false },
]

const distanceCorrespondingValues = {
  percentToValue: {
    0: 0,
    10: 1,
    20: 2,
    30: 3,
    40: 4,
    50: 5,
    60: 10,
    70: 15,
    80: 20,
    90: 30,
    100: 50,
  } as Record<number, number>,
  valueToPercent: {
    0: 0,
    1: 10,
    2: 20,
    3: 30,
    4: 40,
    5: 50,
    10: 60,
    15: 70,
    20: 80,
    30: 90,
    50: 100,
  } as Record<number, number>,
}

const Filters = forwardRef<FiltersHandle, Props>(
  (
    {
      filtersValues,
      onFiltersValuesChange,
      shortFilter,
      isLoading = false,
      manualRequest = false,
      onEraseFilter,
      onSearchButtonClick,
      onClose,
      activeFilterSidebar,
    },
    ref,
  ) => {
    const { t } = useTranslation('search')
    const [enterKeyRefClick] = useEnterKey()
    const displayDistance = useDisplayDistance()
    const [_isFiltersModified, setIsFiltersModified] = useState(false)
    const [isMobile] = useIsResponsive()
    const formRefs = useRef<Record<string, any>>({})
    const [loading, setLoading] = useState(false)

    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const sectionRefs = useRef<Record<OrderFiltersNames | 'petSizes', HTMLDivElement | null>>({
      service: null,
      period: null,
      pets: null,
      distance: null,
      price: null,
      petCount: null,
      sizes: null,
      features: null,
      petSizes: null, // because your accordion key is "petSizes"
    } as any)

    const [errors, setErrors] = useState<Record<string, string>>({})

    // --- Store (commit only on submit) ---
    const hasHydrated = useNewRequestCartStore((s) => s.hasHydrated)
    const setFormSlotsByService = useNewRequestCartStore((s) => s.setFormSlotsByService)
    const setDistance = useNewRequestCartStore((s) => s.setDistance)
    const petsToComplete = useNewRequestCartStore((s) => s.petsToComplete)
    const setPetsToComplete = useNewRequestCartStore((s) => s.setPetsToComplete)
    const setAnimals = useNewRequestCartStore((s) => s.setAnimals)
    const selectedService = useNewRequestCartStore((s) => s.selectedService)
    const setSelectedService = useNewRequestCartStore((s) => s.setSelectedService)
    const storeSlotsByService = useNewRequestCartStore((s) => s.formSlotsByService)
    const setAddress = useNewRequestCartStore((s) => s.setAddress)

    // --- Draft (local) ---
    const [draft, setDraft] = useState<FiltersValues>(filtersValues)
    const [draftSelectedService, setDraftSelectedService] = useState<ServiceSlug | null>(
      (selectedService as ServiceSlug) ?? null,
    )
    const [draftSlotsByService, setDraftSlotsByService] = useState(storeSlotsByService)
    const [draftPetsToComplete, setDraftPetsToComplete] = useState(petsToComplete)
    const [draftDistance, setDraftDistanceState] = useState(filtersValues.distance || { min: 0, max: 20 })

    // Local state for animal counts (dog, cat, nac)
    const [animalCounts, setAnimalCounts] = useState<Record<'dog' | 'cat' | 'nac', number>>(() => ({
      dog: petsToComplete.find((p: any) => p.slug === 'dog')?.number ?? 0,
      cat: petsToComplete.find((p: any) => p.slug === 'cat')?.number ?? 0,
      nac: petsToComplete.find((p: any) => p.slug === 'nac')?.number ?? 0,
    }))

    useEffect(() => {
      if (!activeFilterSidebar) return

      // open correct accordion
      handleAccordionClick(shortFilter as any, true)

      const getScrollableAncestor = (element: HTMLElement | null) => {
        let current = element?.parentElement ?? null

        while (current) {
          const style = window.getComputedStyle(current)
          const overflowY = style.overflowY
          const canScroll =
            (overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight

          if (canScroll) return current
          current = current.parentElement
        }

        return null
      }

      const scrollToRequestedSection = (behavior: ScrollBehavior = 'smooth') => {
        const fallbackContainer = scrollContainerRef.current
        const target = sectionRefs.current[shortFilter as any] as HTMLDivElement | null

        if (!target) return

        const scrollContainer = getScrollableAncestor(target) ?? fallbackContainer

        if (!scrollContainer) {
          target.scrollIntoView({ behavior, block: 'start' })
          return
        }

        const containerRect = scrollContainer.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const headerOffset = isMobile ? 76 : 8
        const nextTop = scrollContainer.scrollTop + (targetRect.top - containerRect.top) - headerOffset

        scrollContainer.scrollTo({
          top: Math.max(0, nextTop),
          behavior,
        })
      }

      // scroll after layout paints
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToRequestedSection('auto')

          // On mobile, drawer/content animations can still shift layout slightly.
          // Re-apply once after opening to keep the section aligned under the header.
          if (isMobile) {
            setTimeout(() => {
              scrollToRequestedSection('auto')
            }, 220)
          }
        })
      })
    }, [activeFilterSidebar, shortFilter, isMobile])

    // ✅ When drawer opens, re-seed drafts from committed values (store + props)
    useEffect(() => {
      if (!activeFilterSidebar) return

      setDraft(filtersValues)
      setDraftSelectedService((selectedService as ServiceSlug) ?? null)
      setDraftSlotsByService(storeSlotsByService)
      setDraftPetsToComplete(petsToComplete)
      setDraftDistanceState(filtersValues.distance || { min: 0, max: 20 })
      setIsFiltersModified(false)
      setErrors({})

      // resync counters UI from committed pets
      setAnimalCounts({
        dog: petsToComplete.find((p: any) => p.slug === 'dog')?.number ?? 0,
        cat: petsToComplete.find((p: any) => p.slug === 'cat')?.number ?? 0,
        nac: petsToComplete.find((p: any) => p.slug === 'nac')?.number ?? 0,
      })
    }, [activeFilterSidebar]) // intentionally only on open/close

    // Helper: patch the local draft (NOT the store)
    const patchDraft = (patch: Partial<FiltersValues>) => {
      setDraft((prev) => ({ ...prev, ...patch }))
      setIsFiltersModified(true)
    }

    // ✅ Slot handlers (draft-only) via injected useServiceSlots
    const {
      currentSlug,
      addNewSlot,
      handleSaveSlot,
      removeSlot,
      updateFormSlotsByService: updateDraftSlotsByService,
    } = useServiceSlots({
      actualService: draftSelectedService,
      formSlotsByService: draftSlotsByService as any,
      updateFormSlotsByService: (updater) => setDraftSlotsByService(updater as any),
    })

    // Ensure at least one blank slot exists for the current service (draft-only)
    useEffect(() => {
      if (!hasHydrated) return
      if (!currentSlug) return
      const currentSlots = (draftSlotsByService as any)?.[currentSlug] ?? []
      if (currentSlots.length > 0) return

      updateDraftSlotsByService((prev: any) => ({
        ...prev,
        [currentSlug]: [makeBlankSlot(currentSlug, null)],
      }))
    }, [hasHydrated, currentSlug, draftSlotsByService, updateDraftSlotsByService])

    // Error check function validating the DRAFT slots (not store)
    const errorCheck = () => {
      let allOK = true
      const newErrors: Record<string, string> = {}

      if (currentSlug) {
        for (const slot of (draftSlotsByService as any)?.[currentSlug] ?? []) {
          const slotRef = formRefs.current[slot.id]
          if (slotRef?.validateOnly) {
            const ok = slotRef.validateOnly()
            if (!ok) allOK = false
          }
        }
      }

      setErrors(newErrors)
      return allOK
    }

    // Submit: validate → commit to store → trigger search
    const handleEnhancedSearchSubmit = async () => {
      setLoading(true)

      const slotsValid = errorCheck()
      if (!slotsValid) {
        setLoading(false)
        return
      }

      const submitted: FiltersValues = {
        ...draft,
        // @ts-expect-error
        services: draftSelectedService ? [draftSelectedService] : undefined, // adapt if your API expects array or single value
        pets: draftPetsToComplete
          ? draftPetsToComplete.filter((pet: any) => pet.number > 0).map((pet: any) => pet.slug)
          : undefined,
        address: draft.address,
        distance: draftDistance,
        period: {
          start: draftSlotsByService?.[draftSelectedService ?? '']?.[0]?.data?.dateStart ?? null,
          end: draftSlotsByService?.[draftSelectedService ?? '']?.[0]?.data?.dateEnd ?? null,
        },
      }

      // ✅ Commit to Zustand ONLY NOW
      setFormSlotsByService({
        promenade: draftSelectedService === 'promenade' ? (draftSlotsByService as any)?.promenade : [],
        'garde-chez-vous':
          draftSelectedService === 'garde-chez-vous' ? (draftSlotsByService as any)?.['garde-chez-vous'] : [],
        'garde-chez-eux':
          draftSelectedService === 'garde-chez-eux' ? (draftSlotsByService as any)?.['garde-chez-eux'] : [],
        visite: draftSelectedService === 'visite' ? (draftSlotsByService as any)?.visite : [],
      })

      setSelectedService(draftSelectedService)
      setPetsToComplete(draftPetsToComplete as any)
      setAnimals([])
      setDistance(submitted.distance || { min: 0, max: 20 })
      setAddress(draft.address as any)

      setLoading(false)

      // ✅ Trigger search with submitted filters
      onSearchButtonClick(submitted)
    }

    // ✅ Expose the submit to parent
    useImperativeHandle(ref, () => ({
      submitDraft: handleEnhancedSearchSubmit,
    }))

    // Accordion state
    const [accordionState, handleAccordionClick] = useAccordion({
      service: true,
      pets: true,
      distance: true,
      price: false,
      petCount: true,
      petSizes: true,
      features: true,
    })

    // Service change: draft only
    const handleServiceChange = (value: string) => {
      const slug = getSlotType(value) as ServiceSlug | null
      if (!slug) return

      setDraftSelectedService(slug)
      setIsFiltersModified(true)

      // Optional: if your API expects services in FiltersValues, keep draft in sync
      // patchDraft({ services: [slug] as any })

      const isRecurrent = slug === 'promenade' || slug === 'visite'
      setDraftSlotsByService((prev: any) => {
        const existing = prev?.[slug] ?? []
        const nextSlots = isRecurrent
          ? [makeBlankSlot(slug, null)]
          : existing.length
          ? existing
          : [makeBlankSlot(slug, null)]
        return { ...prev, [slug]: nextSlots }
      })
    }

    // Animals: draft only
    const handleAnimalChange = (slug: 'dog' | 'cat' | 'nac', delta: number) => {
      setAnimalCounts((prev) => ({ ...prev, [slug]: Math.max(0, prev[slug] + delta) }))
    }

    useEffect(() => {
      const next = [
        { slug: 'dog', number: animalCounts.dog },
        { slug: 'cat', number: animalCounts.cat },
        { slug: 'nac', number: animalCounts.nac },
      ]
      setDraftPetsToComplete(next as any)
      patchDraft({ petCount: animalCounts.dog + animalCounts.cat + animalCounts.nac })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animalCounts.dog, animalCounts.cat, animalCounts.nac])

    // Draft patchers
    const handleAddressChange = (value: any) => patchDraft({ address: value })

    const handlePetSizesChange = (sizeSlug: string) => {
      const updated = (draft.petSize || []).includes(sizeSlug)
        ? (draft.petSize || []).filter((s) => s !== sizeSlug)
        : [...(draft.petSize || []), sizeSlug]
      patchDraft({ petSize: updated })
    }

    const handleFeaturesChange = (featureSlug: string) => {
      const updated = (draft.features || []).includes(featureSlug)
        ? (draft.features || []).filter((f) => f !== featureSlug)
        : [...(draft.features || []), featureSlug]
      patchDraft({ features: updated })
    }

    const handleDistanceFilterChange = (newValue: number[]) => {
      const newDistance = {
        min: distanceCorrespondingValues.percentToValue[newValue[0]],
        max: distanceCorrespondingValues.percentToValue[newValue[1]],
      }
      setDraftDistanceState(newDistance)
      patchDraft({ distance: newDistance })
    }

    // open the requested accordion on mount/change
    useEffect(() => {
      handleAccordionClick(shortFilter, true)
    }, [shortFilter])

    const isIOS =
      typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream

    return (
      <>
        {/* HEADER */}
        <div
          ref={(el) => {
            sectionRefs.current.base = el
          }}
        ></div>
        <Box
          sx={{
            paddingLeft: '10px',
            paddingRight: '30px',
            position: isMobile ? 'fixed' : 'relative',
            top: 0,
            zIndex: 3,
            backgroundColor: isMobile ? '#fff' : 'transparent',
            width: isMobile ? 'calc(100% - 20px)' : '100%',
          }}
        >
          <Box
            sx={{ display: 'flex', padding: '20px 0', justifyContent: 'space-between', alignItems: 'center' }}
            className="EmtReactNativeWebViewMarginTop"
          >
            <EmtTypography variant="h4" fontWeight={800}>
              {t('filters.title')}
            </EmtTypography>
            <EmtIcon className="emt-x-1" onClick={onClose} sx={{ fontSize: '18px', cursor: 'pointer' }} />
          </Box>
        </Box>
        {/* END HEADER */}

        <Box
          ref={scrollContainerRef}
          sx={{
            minWidth: isMobile ? '100%' : '440px',
            overflowX: 'hidden',
            overflowY: 'auto',
            position: 'relative',
            scrollbarColor: '#D2D1D4 transparent',
            paddingRight: '30px',
            paddingLeft: '10px',
            paddingTop: isMobile ? '20px' : '10px',
            paddingBottom: '80px',
            '&::-webkit-scrollbar': { width: '5px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#D2D1D4', borderRadius: '8px' },
          }}
          className="EmtReactNativeWebViewMarginTop"
        >
          <Box className={'EmtReactNativeWebViewPaddingBottom'}>
            {/* Service type */}
            <div
              ref={(el) => {
                sectionRefs.current.service = el
              }}
            >
              <EmtAccordion
                title={'De quel service avez-vous besoin ?'}
                expanded={accordionState.service}
                onClick={() => handleAccordionClick('service')}
              >
                <Box sx={{ minWidth: 0, maxWidth: '100%', flex: 1 }}>
                  <ServiceSection
                    isMobile={isMobile}
                    selectedService={draftSelectedService}
                    formSlotsByService={draftSlotsByService as any}
                    errors={errors}
                    formRefs={formRefs}
                    localStorageInitialValues={null}
                    onServiceChange={handleServiceChange}
                    onAddNewSlot={addNewSlot}
                    onSaveSlot={handleSaveSlot}
                    onRemoveSlot={removeSlot}
                    showSelect
                    addButton={false}
                    showGardeTimes={false}
                  />
                </Box>

                {errors.selectedService && (
                  <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                    {errors.selectedService}
                  </EmtTypography>
                )}
                {errors.dateTimeInstances && (
                  <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                    {errors.dateTimeInstances}
                  </EmtTypography>
                )}
              </EmtAccordion>
            </div>
            {/* END Service type */}

            {/* Pets */}
            <div
              ref={(el) => {
                sectionRefs.current.pets = el
              }}
            >
              <EmtAccordion
                title={'Pour quels animaux ?'}
                expanded={accordionState.pets}
                onClick={() => handleAccordionClick('pets')}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <AnimalIncrement
                    slug="dog"
                    count={animalCounts.dog}
                    onChange={(delta) => handleAnimalChange('dog', delta)}
                  />
                  <AnimalIncrement
                    slug="cat"
                    count={animalCounts.cat}
                    onChange={(delta) => handleAnimalChange('cat', delta)}
                  />
                  <AnimalIncrement
                    slug="nac"
                    count={animalCounts.nac}
                    onChange={(delta) => handleAnimalChange('nac', delta)}
                  />
                </Box>
              </EmtAccordion>
            </div>
            {/* END Pets */}

            {/* Distance */}
            <div
              ref={(el) => {
                sectionRefs.current.distance = el
              }}
            >
              <EmtAccordion
                title={'A quelle adresse ?'}
                expanded={accordionState.distance}
                onClick={() => handleAccordionClick('distance')}
              >
                <AddressAutocomplete
                  label={'Lieu'}
                  address={draft.address && 'formatted' in (draft.address as any) ? (draft.address as any) : null}
                  onAddressChange={(address) => handleAddressChange(address)}
                  iconLeftClassName="emt-pin"
                />

                <Box sx={{ padding: '30px 15px', paddingBottom: '15px' }}>
                  <EmtDistance
                    // @ts-expect-error
                    color="purple"
                    disabled={!manualRequest && isLoading}
                    min={0}
                    max={100}
                    minDistanceBetweenThumbs={10}
                    value={[
                      distanceCorrespondingValues.valueToPercent[draftDistance.min ?? 0],
                      distanceCorrespondingValues.valueToPercent[draftDistance.max ?? 0],
                    ]}
                    onChange={(v: number | number[], _event: Event) => {
                      if (isIOS && _event?.type === 'mousedown') return
                      handleDistanceFilterChange(v as number[])
                    }}
                    marks={[
                      { value: 0, label: displayDistance(0) },
                      { value: 10 },
                      { value: 20 },
                      { value: 30 },
                      { value: 40 },
                      { value: 50 },
                      { value: 60 },
                      { value: 70 },
                      { value: 80 },
                      { value: 100, label: displayDistance(50) },
                    ]}
                    valueLabelFormat={(v: number) =>
                      displayDistance(distanceCorrespondingValues.percentToValue[v] ?? v, '', 1)
                    }
                    step={10}
                  />
                </Box>
              </EmtAccordion>
            </div>
            {/* END Distance */}

            {/* Sizes */}
            <div
              ref={(el) => {
                sectionRefs.current.sizes = el
              }}
            >
              <EmtAccordion
                title={'Tailles acceptées'}
                expanded={accordionState.petSizes}
                onClick={() => handleAccordionClick('petSizes')}
              >
                {petSizes.map((petSize, key) => (
                  <FormControlLabel
                    key={key}
                    disabled={!manualRequest && isLoading}
                    control={
                      <EmtCheckbox
                        onChange={() => handlePetSizesChange(petSize.slug)}
                        checked={(draft.petSize || []).includes(petSize.slug)}
                        controlledCheckbox={true}
                        sx={{
                          backgroundColor: '#F9F9F9',
                          '& .emt-tick': { backgroundColor: '#AE59C6' },
                          '& .emt-icons': { borderColor: '#D2D1D4' },
                        }}
                      />
                    }
                    label={
                      <EmtTypography variant="body2" lineHeight={1} color="greyGradient.main">
                        {petSize.title}
                      </EmtTypography>
                    }
                    sx={accordionCheckboxContainerStyle}
                  />
                ))}
              </EmtAccordion>
            </div>
            {/* END Sizes */}

            {/* Features */}
            <div
              ref={(el) => {
                sectionRefs.current.features = el
              }}
            >
              <EmtAccordion
                title={'Caractéristiques'}
                expanded={accordionState.features}
                onClick={() => handleAccordionClick('features')}
              >
                {features.map((feature, key) => (
                  <FormControlLabel
                    key={key}
                    disabled={!manualRequest && isLoading}
                    control={
                      <EmtCheckbox
                        onChange={() => handleFeaturesChange(feature.slug)}
                        checked={(draft.features || []).includes(feature.slug)}
                        controlledCheckbox={true}
                        sx={{
                          backgroundColor: '#F9F9F9',
                          '& .emt-tick': { backgroundColor: '#AE59C6' },
                          '& .emt-icons': { borderColor: '#D2D1D4' },
                        }}
                      />
                    }
                    label={
                      <EmtTypography
                        variant="body2"
                        lineHeight={1}
                        color="greyGradient.main"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {feature.title}
                      </EmtTypography>
                    }
                    sx={accordionCheckboxContainerStyle}
                  />
                ))}
              </EmtAccordion>
            </div>
            {/* END Features */}
          </Box>
        </Box>

        {/* BUTTONS */}
        <Box
          sx={{
            position: 'sticky',
            zIndex: 2,
            left: '0',
            transform: 'translateX(-20px)',
            width: 'calc(100% + 20px)',
            backgroundColor: '#F9F9F9',
            bottom: 0,
            boxShadow: '0px 12px 16px rgba(0, 0, 0, 0.08), 0px 4px 56px rgba(0, 0, 0, 0.08)',
          }}
          className={'EmtReactNativeWebViewPaddingBottom'}
        >
          <ActionButtonsWrapper>
            <Box sx={{ width: '30%' }}>
              <EmtButton size="large" color="purple" variant="outlined" onClick={onEraseFilter} fullWidth>
                {t('filters.buttons.erase')}
              </EmtButton>
            </Box>

            <Box sx={{ width: '70%' }}>
              <EmtButton
                ref={enterKeyRefClick}
                size="large"
                color="purple"
                startIcon={<EmtIcon className="emt-search-1" />}
                onClick={handleEnhancedSearchSubmit}
                fullWidth
                loader={loading || isLoading}
                // disabled={!isFiltersModified}
              >
                {t('filters.buttons.search')}
              </EmtButton>
            </Box>
          </ActionButtonsWrapper>
        </Box>
        {/* END BUTTONS */}
      </>
    )
  },
)

Filters.displayName = 'Filters'
export default Filters
